import React, { FunctionComponent, useEffect, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';

import clsx from 'clsx';
import { useController, useForm } from 'react-hook-form';

import { CreateProfileAddressPayload } from '@domains/addresses';
import { City } from '@domains/common-data';

import { setIsOpenAddAddress } from '@stores/dialogs';
import { useCommonData } from '@stores/global-data';
import { $profile, createAddressesFx, updateAddressesFx } from '@stores/profile';

import { VALIDATION_MESSAGES } from '@utils/constants';
import { handleFormError } from '@utils/form';
import { isValueSet } from '@utils/guards';
import { SnackBar } from '@utils/snackbar';

import { Button } from '@components/common/Button/Button';
import { StyledInput } from '@components/common/StyledInput';
import { AddressCityPicker } from '@components/profile/AddresCityPicker/AddresCityPicker';

import styles from './AddressAddForm.module.scss';

import { AddressSearchInput } from '../AddressSearchInput/AddressSearchInput';

const selectCityAsDefault = (cities: City[], cityName: string): City =>
    pipe(
        cities,
        A.findFirst(({ name }) => name === cityName),
        O.getOrElse(() => cities[0]),
    );

type AddressFormValues = {
    street: string;
    house: string;
    entrance: string;
    apartment: string;
    floor: string;
};

const initialValues: AddressFormValues = {
    street: '',
    house: '',
    entrance: '',
    apartment: '',
    floor: '',
};

export const AddressAddForm: FunctionComponent = () => {
    const { cities } = useCommonData();
    const [selectedCity, setSelectedCity] = useState<City | null>(null);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    const { editedAddress, defaultCitySelectedName } = useUnit($profile);
    const setIsOpenAddAddressFn = useUnit(setIsOpenAddAddress);
    const updateAddresses = useUnit(updateAddressesFx);
    const createAddresses = useUnit(createAddressesFx);

    const submitButtonText = isValueSet(editedAddress) ? 'Сохранить' : 'Добавить';

    const form = useForm<AddressFormValues>({
        defaultValues: initialValues,
        shouldUnregister: true,
        mode: 'onChange',
    });

    const { control, handleSubmit, reset, setValue, clearErrors } = form;

    const { fieldState: houseFieldState } = useController({
        name: 'house',
        control,
        rules: {
            required: VALIDATION_MESSAGES.streetHouse,
        },
    });

    const { fieldState: streetFieldState } = useController({
        name: 'street',
        control,
        rules: {
            required: VALIDATION_MESSAGES.streetHouse,
        },
    });

    const { field: entranceField } = useController({
        name: 'entrance',
        control,
    });

    const { field: apartmentField } = useController({
        name: 'apartment',
        control,
    });

    const { field: floorField } = useController({
        name: 'floor',
        control,
    });

    const handleCityPickerChange = (city: City) => {
        setSelectedCity(city);
        reset();
    };

    const onSubmit = async (data: AddressFormValues) => {
        const { street, house, entrance, apartment, floor } = data;

        if (selectedCity === null) return;

        const payload: CreateProfileAddressPayload = {
            city_id: selectedCity.id,
            street,
            house,
        };

        if (entrance.length > 0) {
            payload.entrance = entrance;
        }

        if (apartment.length > 0) {
            payload.apartment = apartment;
        }

        if (floor.length > 0) {
            payload.floor = floor;
        }

        setIsFormSubmitting(true);

        try {
            if (editedAddress !== null) {
                await updateAddresses({ ...payload, id: editedAddress.id });
            } else {
                await createAddresses(payload);
            }

            SnackBar.success('Адрес доставки сохранен');
            reset();
            setIsOpenAddAddressFn(false);
        } catch (error) {
            SnackBar.handleApiError(error);
            handleFormError(form, error);
        } finally {
            setIsFormSubmitting(false);
        }
    };

    useEffect(() => {
        if (editedAddress === null && cities.length > 0) {
            setSelectedCity(selectCityAsDefault(cities, defaultCitySelectedName));
        }
    }, [cities, defaultCitySelectedName]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (editedAddress !== null && cities.length > 0) {
            const city = cities.find((item) => item.id === editedAddress.city_id);

            if (city !== undefined) {
                setSelectedCity(city);
            }

            setValue('street', editedAddress.street);
            setValue('house', editedAddress.house);
            setValue('entrance', editedAddress.entrance ?? '');
            setValue('apartment', editedAddress.apartment ?? '');
            setValue('floor', editedAddress.floor ?? '');
        }
    }, [cities, editedAddress, setValue]);

    return (
        <form className={styles.root} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.grid}>
                {selectedCity !== null && (
                    <>
                        <AddressCityPicker
                            className={styles.cityInput}
                            items={cities}
                            selectedItem={selectedCity}
                            onChange={handleCityPickerChange}
                        />
                        <AddressSearchInput
                            className={clsx(styles.streetInput, styles.isFullWidth)}
                            cityFiasId={selectedCity.fias_id}
                            onChange={(value) => {
                                setValue('street', value !== null ? value.street : '');
                                setValue(
                                    'house',
                                    value !== null && value.house !== null ? value.house : '',
                                );
                                clearErrors(['street', 'house']);
                            }}
                            hintMessage={
                                streetFieldState.error?.message || houseFieldState.error?.message
                            }
                            hasError={Boolean(
                                streetFieldState.error?.message !== undefined ||
                                    houseFieldState.error?.message,
                            )}
                        />
                    </>
                )}

                <StyledInput {...entranceField} className={styles.entranceInput} label="Подъезд" />
                <StyledInput {...floorField} className={styles.floorInput} label="Этаж" />
                <StyledInput
                    {...apartmentField}
                    className={styles.apartmentInput}
                    label="Кв/офис"
                />
            </div>
            <Button
                isLoading={isFormSubmitting}
                className={styles.button}
                type="submit"
                variant="green"
                isFullWith
            >
                {submitButtonText}
            </Button>
        </form>
    );
};
